'use client';

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Upload, X, FileText, Download } from 'lucide-react';
import { useImageUpload } from '@/hooks/useImageUpload';
import { useUpdateBusinessMutation } from '@/api/mutations/updateBusiness';
import { useUpdateEducationMutation } from '@/api/mutations/updateEducation';
import { toast } from 'sonner';

const MAX_DOCUMENTS = 10;
const ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv';

export interface BusinessDocument {
  key: string;
  name: string;
  size?: number;
}

interface BusinessDocumentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  business: any;
  canEdit: boolean;
  entityType?: 'business' | 'education';
}

const formatBytes = (bytes?: number) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function BusinessDocumentsDialog({
  open,
  onOpenChange,
  business,
  canEdit,
  entityType = 'business',
}: BusinessDocumentsDialogProps) {
  const [items, setItems] = useState<BusinessDocument[]>(
    business.documents || []
  );
  const [isUploading, setIsUploading] = useState(false);

  const isEducation = entityType === 'education';
  const { uploadImage } = useImageUpload({
    purpose: 'businesses',
  });
  const updateBusinessMutation = useUpdateBusinessMutation(business.id);
  const updateEducationMutation = useUpdateEducationMutation(business.id);

  useEffect(() => {
    setItems(business.documents || []);
  }, [business.documents]);

  const persist = async (next: BusinessDocument[]) => {
    if (isEducation) {
      await updateEducationMutation.mutateAsync({
        documents: next,
        name: business.name,
      } as any);
    } else {
      await updateBusinessMutation.mutateAsync({
        documents: next,
        name: business.name,
        description: business.description,
      } as any);
    }
  };

  const handleFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    if (items.length + files.length > MAX_DOCUMENTS) {
      toast.error(`Maximum ${MAX_DOCUMENTS} documents allowed`);
      return;
    }

    setIsUploading(true);
    try {
      const uploaded: BusinessDocument[] = [];
      for (const file of files) {
        const key = await uploadImage(file);
        if (key) uploaded.push({ key, name: file.name, size: file.size });
      }
      const next = [...items, ...uploaded];
      setItems(next);
      await persist(next);
    } catch {
      toast.error('Failed to upload document');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = async (index: number) => {
    const previous = items;
    const next = items.filter((_, i) => i !== index);
    setItems(next);
    try {
      await persist(next);
    } catch {
      toast.error('Failed to remove document');
      setItems(previous);
    }
  };

  const remaining = MAX_DOCUMENTS - items.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl rounded-2xl sm:rounded-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Documents {canEdit && `(${items.length}/${MAX_DOCUMENTS})`}
          </DialogTitle>
        </DialogHeader>

        {items.length === 0 && !canEdit && (
          <div className="text-center py-10 text-[#554E49]">
            No documents yet
          </div>
        )}

        {items.length > 0 && (
          <ul className="flex flex-col gap-2">
            {items.map((doc, index) => (
              <li
                key={`${doc.key}-${index}`}
                className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2"
              >
                <FileText className="h-5 w-5 text-gray-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{doc.name}</p>
                  {doc.size != null && (
                    <p className="text-xs text-[#554E49]">
                      {formatBytes(doc.size)}
                    </p>
                  )}
                </div>
                <a
                  href={doc.key}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={doc.name}
                  className="p-2 rounded-full hover:bg-gray-100 text-gray-700"
                  aria-label="Download"
                >
                  <Download className="h-4 w-4" />
                </a>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    className="p-2 rounded-full hover:bg-red-50 text-red-500"
                    aria-label="Remove"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {canEdit && remaining > 0 && (
          <div className="mt-4">
            <label
              htmlFor="business-documents-upload"
              tabIndex={0}
              role="button"
              aria-label="Click or press enter to upload documents"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  document.getElementById('business-documents-upload')?.click();
                }
              }}
              className="flex flex-col items-center gap-2 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {isUploading ? (
                <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
              ) : (
                <Upload className="h-8 w-8 text-gray-500" />
              )}
              <p className="text-sm text-[#554E49]">
                {isUploading ? 'Uploading...' : 'Click to upload documents'}
              </p>
              <p className="text-xs text-[#554E49]">
                PDF, DOC, XLS, PPT, TXT · {remaining} slot
                {remaining > 1 ? 's' : ''} remaining
              </p>
            </label>
            <input
              id="business-documents-upload"
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={handleFilesChange}
              disabled={isUploading}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
